import { Action, Dispatch } from 'redux'
import { push } from 'connected-react-router'
import { auth, db, FirebaseDocRefType, FirebaseTimestamp, staffsRef, storesRef } from 'firebase/index'
import { State } from 'index'
import { signInAction } from 'reducks/auth/actions'
import { hideLoadingAction, showLoadingAction } from 'reducks/loading/actions'
import { UNAUTHORIZED_URLS } from 'constants/auth'
import dexieDb from 'lib/db'
import { getDeviceInfo, getIsWebView } from 'lib/helper'
import { StaffDeviceTokenType, StaffPushSettingType, StaffType } from 'types/staff'

const listenAuthState = () => {
  return async (dispatch: Dispatch<Action>, getState: () => State) => {
    const path = getState().router.location.pathname
    dispatch(showLoadingAction())
    return auth.onAuthStateChanged(async (user) => {
      // 未認証であればログインページへリダイレクト
      if (!user) {
        dispatch(hideLoadingAction())
        if (UNAUTHORIZED_URLS.includes(path)) return
        dispatch(push('/login'))
      } else {
        // スタッフの情報を取得
        const staff = await staffsRef
          .doc(user.uid)
          .get()
          .then((snapshot) => snapshot.data() as StaffType)

        if (!staff) {
          dispatch(hideLoadingAction())
          throw new Error('スタッフが存在しません')
        }

        const defaultStore = staff.roles[0]

        // スーパー管理者の場合はストアへの参照がない
        if (!defaultStore) {
          const dexieAuth = await dexieDb.auth.get(user.uid)
          // indexedDBにデータが存在しない場合は初期値をセット
          if (!dexieAuth || !dexieAuth.store_id) {
            dispatch(
              signInAction({
                is_admin: staff.is_admin,
                is_signed_in: true,
                mails: staff.mails,
                role: 0,
                roles: staff.roles,
                brand_ref: undefined,
                shop_ref: undefined,
                store_ref: undefined,
                switching_store_refs: [],
                uid: user.uid,
                device_tokens: staff.device_tokens,
                push_settings: staff.push_settings,
                top_tab: 'hand_over',
                default_bread_types: ['daily', 'preorder', 'delivery'],
              })
            )
            if (/^(?!\/admin).*$/.test(path)) {
              dispatch(push('/admin/shops'))
            }
          } else {
            dispatch(
              signInAction({
                is_admin: staff.is_admin,
                is_signed_in: true,
                mails: staff.mails,
                role: dexieAuth.role,
                roles: staff.roles,
                brand_ref: dexieAuth.brand_id ? db.collection('brands').doc(dexieAuth.brand_id) : undefined,
                shop_ref: dexieAuth.shop_id ? db.collection('shops').doc(dexieAuth.shop_id) : undefined,
                store_ref: dexieAuth.store_id ? db.collection('stores').doc(dexieAuth.store_id) : undefined,
                switching_store_refs: [],
                uid: user.uid,
                device_tokens: staff.device_tokens,
                push_settings: staff.push_settings,
                top_tab: 'hand_over',
                default_bread_types: ['daily', 'preorder', 'delivery'],
              })
            )
          }
        } else {
          // アプリの場合、デバイストークンを取得して送信する
          if (!staff.is_admin && getIsWebView()) {
            const result = (await getDeviceInfo()) as any

            if (result && result.device_token && result.device_type) {
              const device_token = result.device_token
              const device_type = result.device_type

              const prevDeviceToken = staff.device_tokens
                ? staff.device_tokens.find((deviceToken) => deviceToken.device_token === device_token)
                : null
              if (!prevDeviceToken) {
                //未登録なので送信する
                const deviceToken: StaffDeviceTokenType = {
                  device_token: device_token,
                  device_type: device_type,
                  created_at: FirebaseTimestamp.now(),
                }
                if (!staff.device_tokens) staff.device_tokens = []
                staff.device_tokens.push(deviceToken)
                await staffsRef.doc(staff.uid).set(staff)
              }

              //プッシュ通知設定があるか確認して、なかったらテンプレートを保存する
              if (staff.mails && staff.mails.length > 0) {
                let is_update_push_settings = false
                staff.mails.forEach(function (mail) {
                  const tmpPushSetting = staff.push_settings
                    ? staff.push_settings.find((_pushSetting) => {
                        if (typeof mail.manufacturer_ref !== 'undefined') {
                          if (
                            _pushSetting.manufacturer_ref &&
                            mail.manufacturer_ref.id == _pushSetting.manufacturer_ref.id &&
                            mail.store_ref.id == _pushSetting.store_ref.id
                          ) {
                            return true
                          }
                        } else {
                          if (mail.store_ref.id == _pushSetting.store_ref.id) {
                            return true
                          }
                        }
                        return false
                      })
                    : null
                  if (!tmpPushSetting) {
                    //設定がないので追加
                    let pushSetting: StaffPushSettingType
                    if (mail.manufacturer_ref) {
                      pushSetting = {
                        store_ref: mail.store_ref,
                        manufacturer_ref: mail.manufacturer_ref,
                        permissions: ['push_order_daily', 'push_order_preorder', 'push_remind_preorder'],
                      }
                    } else {
                      pushSetting = {
                        store_ref: mail.store_ref,
                        permissions: ['push_order_daily', 'push_order_preorder', 'push_remind_preorder'],
                      }
                    }
                    if (!staff.push_settings) staff.push_settings = []
                    staff.push_settings.push(pushSetting)
                    is_update_push_settings = true
                  }
                })
                if (is_update_push_settings) {
                  await staffsRef.doc(staff.uid).update({ push_settings: staff.push_settings })
                }
              }
            }
          }

          // 削除済みのストアにはアクセスできないようにする
          const deletedStores: FirebaseDocRefType[] = []
          await storesRef
            .where('status', '==', 'deleted')
            .get()
            .then((snapshots) => {
              snapshots.forEach((snapshot) => {
                deletedStores.push(snapshot.ref)
              })
            })

          // 削除済みのストアを除外
          const roles = staff.roles.filter((role) => {
            return deletedStores.every((ref) => ref.id !== role.store_ref.id)
          })
          if (roles.length === 0) return

          dispatch(
            signInAction({
              is_admin: staff.is_admin,
              is_signed_in: true,
              mails: staff.mails,
              role: defaultStore.role,
              roles: roles,
              brand_ref: defaultStore.brand_ref,
              shop_ref: undefined,
              store_ref: defaultStore.store_ref,
              switching_store_refs: [],
              uid: user.uid,
              device_tokens: staff.device_tokens,
              push_settings: staff.push_settings,
              top_tab: 'hand_over',
              default_bread_types: ['daily', 'preorder', 'delivery'],
            })
          )
          // 認証済みの状態でログインページまたはパスワードリセットにアクセスしたらトップページへリダイレクト
          if (path === '/login' || path === '/forget' || path === '/forget/confirm') {
            dispatch(push('/'))
          }
        }
        dispatch(hideLoadingAction())
      }
    })
  }
}

export default listenAuthState
